import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from 'react-router'
import trackEvents from '../../constants/track-events'
import { useNotificationContext } from '../../context'
import { trackEvent } from '../../helpers/analytics'
import { useSetState } from '../../hooks/use-set-state'
import SelectMenu from '../select-menu'
import Button from '../tailwind/Button'

const LIKERT_SCALE = {
  0: 'team_analyse.new.research_likert_scale_0',
  1: 'team_analyse.new.research_likert_scale_1',
  2: 'team_analyse.new.research_likert_scale_2',
  3: 'team_analyse.new.research_likert_scale_3',
  4: 'team_analyse.new.research_likert_scale_4'
}

const QUESTIONS = [
  {
    id: '1',
    question: 'team_analyse.new.research_questions_1',
    scale: LIKERT_SCALE
  },
  {
    id: '2',
    question: 'team_analyse.new.research_questions_2',
    scale: LIKERT_SCALE
  },
  {
    id: '3',
    question: 'team_analyse.new.research_questions_3',
    scale: LIKERT_SCALE
  },
  {
    id: '4',
    question: 'team_analyse.new.research_questions_4',
    scale: LIKERT_SCALE
  },
  {
    id: '5',
    question: 'team_analyse.new.research_questions_5',
    scale: LIKERT_SCALE
  },
  {
    id: '6',
    question: 'team_analyse.new.research_questions_6',
    scale: LIKERT_SCALE
  },
  {
    id: '7',
    question: 'team_analyse.new.research_questions_7',
    scale: LIKERT_SCALE
  },
  {
    id: '8',
    question: 'team_analyse.new.research_questions_8',
    scale: LIKERT_SCALE
  }
]

const Research = ({ analyse, analyseMutation }) => {
  const { t } = useTranslation()
  const { alert } = useNotificationContext()

  const navigate = useNavigate()

  const [submitting, setSubmitting] = useState(null)
  const [answers, setAnswers] = useSetState(
    analyse.research_answers || { duration: '0-1', remote: 'NO' }
  )

  const next = () => {
    alert(
      t('team_analyse.new.research.modal_title'),
      t('team_analyse.new.research.modal_description'),
      [
        {
          onPress: () => {
            setSubmitting('next')

            const input = {
              id: analyse.id,
              input: JSON.stringify({
                ...JSON.parse(analyse.input),
                status: 'ACTIVE',
                research_answers: answers
              })
            }

            analyseMutation.mutate(
              { input },
              {
                onSuccess: ({ id }) => {
                  setSubmitting(null)
                  trackEvent(trackEvents.COMPLETE_CREATE_TEAMANALYSIS)
                  navigate(`/team-analyse/${id}`, {
                    state: { showIntro: true },
                    replace: true
                  })
                },
                onError: () => setSubmitting(null)
              }
            )
          },
          text: t('team_analyse.new.research.modal_submit_action')
        },
        {
          text: t('team_analyse.new.research.modal_cancel_action'),
          style: 'cancel'
        }
      ]
    )
  }

  const back = () => {
    setSubmitting('back')

    const input = {
      id: analyse.id,
      input: JSON.stringify({
        ...JSON.parse(analyse.input),
        status: 'WAIT_SPACES',
        research_answers: answers
      })
    }

    analyseMutation.mutate(
      { input },
      {
        onSuccess: () => setSubmitting(null),
        onError: () => setSubmitting(null)
      }
    )
  }

  return (
    <div className='mx-auto max-w-2xl pb-32'>
      <span className='text-3xl font-extrabold tracking-tight text-gray-900'>
        {t('team_analyse.new.research.title')}
      </span>
      <p className='mt-4 max-w-lg rounded-xl bg-primaryBlue p-4 text-sm text-white'>
        {t('team_analyse.new.research.description')}
      </p>
      <div className='mt-12 flex flex-col gap-y-8'>
        {QUESTIONS.map(({ id: question_id, question, scale }, index) => (
          <div key={index} className='py-4'>
            <span className='block text-base text-gray-900'>{t(question)}</span>
            <div className='relative mt-2 justify-between px-4 md:flex'>
              {Object.keys(scale).map((scale_key, index) => (
                <div
                  key={index}
                  onClick={() => setAnswers({ [question_id]: scale_key })}
                  className='mt-2 cursor-pointer flex-col items-center md:mt-0 md:flex'
                >
                  <div className='inline-block h-6 w-6 cursor-pointer items-center justify-center rounded-full bg-white md:flex'>
                    <input
                      type='radio'
                      checked={answers[question_id] === scale_key}
                      readOnly
                      className='h-4 w-4 cursor-pointer border-gray-300 text-blue-600 focus:ring-white'
                    />
                  </div>
                  <label className='cursor-pointer text-xs font-semibold text-gray-700'>
                    {t(scale[scale_key])}
                  </label>
                </div>
              ))}
              <div
                // 10 - between top-2 & top-3
                style={{ top: 10 }}
                className='absolute left-0 -z-10 hidden h-1 w-full bg-gray-200 md:block'
              />
            </div>
          </div>
        ))}
      </div>
      <div className='mt-12 flex-col'>
        <SelectMenu
          id='remote'
          label={t('team_analyse.new.research_home_office')}
          options={[
            ['YES', t('global.yes')],
            ['NO', t('global.no')]
          ]}
          translate={false}
          defaultValue={answers.remote}
          onChange={(option) => setAnswers({ remote: option })}
        />
        <SelectMenu
          className={'mt-8'}
          id='duration'
          label={t('team_analyse.new.research_work_length')}
          options={[
            ['0-1', t('team_analyse.new.research_work_length_select_option_1')],
            ['1-2', t('team_analyse.new.research_work_length_select_option_2')],
            ['2-3', t('team_analyse.new.research_work_length_select_option_3')],
            ['3-4', t('team_analyse.new.research_work_length_select_option_4')],
            ['4-X', t('team_analyse.new.research_work_length_select_option_5')]
          ]}
          translate={false}
          defaultValue={answers.duration}
          onChange={(option) => setAnswers({ duration: option })}
        />
      </div>
      <div className='mt-12 flex justify-end gap-x-2 sm:mt-16'>
        <Button.SecondaryLG
          onClick={back}
          text={t('team_analyse.new.back_action')}
          isLoading={submitting === 'back'}
        />
        <Button.PrimaryLG
          text={t('team_analyse.new.finish_action')}
          onClick={next}
          disabled={Object.keys(answers).length !== QUESTIONS.length + 2}
          isLoading={submitting === 'next'}
        />
      </div>
    </div>
  )
}

Research.propTypes = {
  analyse: PropTypes.object.isRequired,
  analyseMutation: PropTypes.object.isRequired
}

export default Research
